/**
 * Inverse computation for the order-aware IScreenDesign collab ops — the engine
 * behind per-user collaborative undo.
 *
 * `invertCollabOps(prevTree, ops, makeOpId)` returns the ops that undo `ops`:
 * applying `ops` to a tree deep-equal to `prevTree` and then applying the returned
 * list yields a tree deep-equal to `prevTree` again (the INVERT LAW). The inverses
 * come back in REVERSE order of `ops` (last op inverted first), so ops that depend
 * on earlier ops in the batch are undone before their prerequisites.
 *
 * Inverses are computed by SIMULATION: `prevTree` is cloned, and for each op (in
 * order) the inverse is recorded against the CURRENT simulated state, after which
 * the op is applied to the simulation via `applyCollabOps`. An op the applier
 * SKIPS (missing target, unresolvable path, cycle, echo …) contributes NO inverse —
 * a skipped op changed nothing, so there is nothing to undo. Neither input is
 * mutated.
 *
 * Positional contracts:
 *  - Node restores (`nodeRemove` → `nodeAdd`) and move-backs (`nodeMove` →
 *    `nodeMove`) are anchored via `afterSiblingId` (`null` = it was first), NEVER
 *    `resolvedIndex`: concurrent edits may shift indices between do and undo, and
 *    anchor-relative insertion is the collab contract. No returned op carries a
 *    `resolvedIndex`.
 *  - ARRAY element ops restore order exactly. In-place element replacement and
 *    tail append/removal invert at element granularity; a NON-tail element removal
 *    inverts to ONE whole-array `propSet` of the pre-op array, because an
 *    element-level set could only re-append at the tail (a keyed selector that
 *    matches nothing APPENDS) and would break the invert law.
 *  - A `propSet` whose path crosses a missing (or `null`) plain-object link — where
 *    `setAtPath` creates intermediate `{}` objects — inverts by removing (or
 *    null-restoring) the TOPMOST created link, not the leaf, so no empty
 *    intermediate objects survive the undo.
 */

import { IDisplayItem } from "../../models/display-item";
import { IScreenDesign } from "../../models/screen-design";
import { applyCollabOps } from "./sd-apply";
import { cloneJsonValue, CollabJsonValue, isJsonArray, isJsonObject } from "./sd-json";
import { ICollabApplyResult, ICollabOp } from "./sd-patch-types";
import {
  ARRAY_INDEX_PATTERN,
  buildPointer,
  CollabPathSegment,
  findSelectorIndex,
  normalizeNodeId,
  parsePointer,
  selectorMatchesElement
} from "./sd-path";

/** An inverse op before it is granted a fresh opId (assigned only to KEPT inverses). */
type CollabOpDraft = Omit<ICollabOp, "opId">;

interface IInvertNodeLocation {
  node: IDisplayItem;
  siblings: IDisplayItem[];
  index: number;
  /** Canonical id of the owning parent; "" for the screen-root sibling list. */
  parentIdNorm: string;
}

export interface IValueSetInverse {
  kind: "set";
  path: string;
  value: CollabJsonValue;
}

export interface IValueRemoveInverse {
  kind: "remove";
  path: string;
}

/** Exported (with the two invert* helpers below) for reuse by the wf-patch inverter. */
export type ValueInverse = IValueSetInverse | IValueRemoveInverse;

function locateNodeWithParent(
  children: IDisplayItem[],
  idNorm: string,
  parentIdNorm: string
): IInvertNodeLocation | null {
  for (let i = 0; i < children.length; i++) {
    const node: IDisplayItem = children[i];
    if (normalizeNodeId(node.id) === idNorm) {
      return { node, siblings: children, index: i, parentIdNorm };
    }
    if (Array.isArray(node.children)) {
      const found: IInvertNodeLocation | null = locateNodeWithParent(node.children, idNorm, normalizeNodeId(node.id));
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
}

/** Anchor for restoring a node to its recorded slot: preceding sibling id, null when first. */
function precedingSiblingId(location: IInvertNodeLocation): string | null {
  if (location.index === 0) {
    return null;
  }
  return normalizeNodeId(location.siblings[location.index - 1].id);
}

/**
 * Whole-array fallback: restore the pre-op array with one `propSet` of the parent
 * path. Always exact; used where element-level ops cannot reproduce order.
 */
function wholeArrayInverse(
  segments: readonly CollabPathSegment[],
  array: readonly CollabJsonValue[]
): ValueInverse | null {
  const parentSegments: readonly CollabPathSegment[] = segments.slice(0, -1);
  if (parentSegments.length === 0) {
    // The root itself would have to be replaced — unreachable for object roots.
    return null;
  }
  return { kind: "set", path: buildPointer(parentSegments), value: cloneJsonValue(array) };
}

/**
 * Inverse of `setAtPath(root, path, newValue)` recorded against the PRE-op state.
 * The parent walk mirrors `walkToParent(createMissing=true)`: a missing plain-object
 * link is where setAtPath will create an intermediate `{}` — the exact inverse then
 * removes (or null-restores) that TOPMOST created link. Returns null when setAtPath
 * would fail (the op will be skipped and needs no inverse).
 */
export function invertValueSet(
  root: unknown,
  segments: readonly CollabPathSegment[],
  newValue: unknown
): ValueInverse | null {
  let container: unknown = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment: CollabPathSegment = segments[i];
    if (segment.kind === "selector") {
      if (!isJsonArray(container)) {
        return null;
      }
      const selectorIndex: number = findSelectorIndex(container, segment);
      if (selectorIndex < 0) {
        return null;
      }
      container = container[selectorIndex];
    } else if (isJsonArray(container)) {
      if (!ARRAY_INDEX_PATTERN.test(segment.key)) {
        return null;
      }
      const index: number = Number(segment.key);
      if (index >= container.length) {
        return null;
      }
      container = container[index];
    } else if (isJsonObject(container)) {
      const existing: CollabJsonValue = container[segment.key];
      if (existing === undefined) {
        return { kind: "remove", path: buildPointer(segments.slice(0, i + 1)) };
      }
      if (existing === null) {
        return { kind: "set", path: buildPointer(segments.slice(0, i + 1)), value: null };
      }
      container = existing;
    } else {
      return null;
    }
  }
  const last: CollabPathSegment = segments[segments.length - 1];
  const path: string = buildPointer(segments);
  if (last.kind === "selector") {
    if (!isJsonArray(container)) {
      return null;
    }
    const payloadKeepsSelector: boolean = selectorMatchesElement(newValue as CollabJsonValue, last);
    if (!payloadKeepsSelector) {
      // The written element will no longer answer to this selector, so an
      // element-level inverse could never find it back — restore the whole array.
      return wholeArrayInverse(segments, container);
    }
    const selectorIndex: number = findSelectorIndex(container, last);
    if (selectorIndex >= 0) {
      return { kind: "set", path, value: cloneJsonValue(container[selectorIndex]) };
    }
    // No match → setAtPath APPENDS at the tail; splicing it back out by selector is exact.
    return { kind: "remove", path };
  }
  if (isJsonArray(container)) {
    if (!ARRAY_INDEX_PATTERN.test(last.key)) {
      return null;
    }
    const index: number = Number(last.key);
    if (index > container.length) {
      return null;
    }
    if (index === container.length) {
      // Tail growth — removing the new tail element is exact.
      return { kind: "remove", path };
    }
    return { kind: "set", path, value: cloneJsonValue(container[index]) };
  }
  if (isJsonObject(container)) {
    const prior: CollabJsonValue = container[last.key];
    if (prior === undefined) {
      return { kind: "remove", path };
    }
    return { kind: "set", path, value: cloneJsonValue(prior) };
  }
  return null;
}

/**
 * Inverse of `removeAtPath(root, path)` recorded against the PRE-op state. The
 * parent walk mirrors `walkToParent(createMissing=false)`: any broken link means
 * the removal will fail (skipped, no inverse). An absent prior value also yields
 * no inverse — under JSON semantics there is nothing to restore.
 */
export function invertValueRemove(root: unknown, segments: readonly CollabPathSegment[]): ValueInverse | null {
  let container: unknown = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment: CollabPathSegment = segments[i];
    if (segment.kind === "selector") {
      if (!isJsonArray(container)) {
        return null;
      }
      const selectorIndex: number = findSelectorIndex(container, segment);
      if (selectorIndex < 0) {
        return null;
      }
      container = container[selectorIndex];
    } else if (isJsonArray(container)) {
      if (!ARRAY_INDEX_PATTERN.test(segment.key)) {
        return null;
      }
      const index: number = Number(segment.key);
      if (index >= container.length) {
        return null;
      }
      container = container[index];
    } else if (isJsonObject(container)) {
      const existing: CollabJsonValue = container[segment.key];
      if (existing === undefined || existing === null) {
        return null;
      }
      container = existing;
    } else {
      return null;
    }
  }
  const last: CollabPathSegment = segments[segments.length - 1];
  const path: string = buildPointer(segments);
  if (last.kind === "selector") {
    if (!isJsonArray(container)) {
      return null;
    }
    const selectorIndex: number = findSelectorIndex(container, last);
    if (selectorIndex < 0) {
      return null;
    }
    if (selectorIndex === container.length - 1) {
      // Tail element: after the splice the selector matches nothing, so an
      // element-level set APPENDS — exactly where the element was.
      return { kind: "set", path, value: cloneJsonValue(container[selectorIndex]) };
    }
    return wholeArrayInverse(segments, container);
  }
  if (isJsonArray(container)) {
    if (!ARRAY_INDEX_PATTERN.test(last.key)) {
      return null;
    }
    const index: number = Number(last.key);
    if (index >= container.length) {
      return null;
    }
    if (index === container.length - 1) {
      return { kind: "set", path, value: cloneJsonValue(container[index]) };
    }
    return wholeArrayInverse(segments, container);
  }
  if (isJsonObject(container)) {
    const prior: CollabJsonValue = container[last.key];
    if (prior === undefined) {
      return null;
    }
    return { kind: "set", path, value: cloneJsonValue(prior) };
  }
  return null;
}

function invertNodeAdd(op: ICollabOp): CollabOpDraft | null {
  const payload: unknown = op.nodePayload;
  if (!isJsonObject(payload)) {
    return null;
  }
  const payloadId: CollabJsonValue = payload.id;
  if (typeof payloadId !== "string" && typeof payloadId !== "number") {
    return null;
  }
  const idNorm: string = normalizeNodeId(payloadId);
  if (idNorm === "") {
    return null;
  }
  // The payload id is what the tree will hold (post-any-remap as given in the op);
  // removing that root takes the whole added subtree with it.
  return { kind: "nodeRemove", nodeId: idNorm };
}

function invertNodeRemove(simulation: IScreenDesign, op: ICollabOp): CollabOpDraft | null {
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return null;
  }
  const location: IInvertNodeLocation | null = locateNodeWithParent(simulation.displayItems, idNorm, "");
  if (location === null) {
    return null;
  }
  return {
    kind: "nodeAdd",
    nodeId: idNorm,
    parentId: location.parentIdNorm,
    afterSiblingId: precedingSiblingId(location),
    nodePayload: cloneJsonValue(location.node)
  };
}

function invertNodeMove(simulation: IScreenDesign, op: ICollabOp): CollabOpDraft | null {
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return null;
  }
  const location: IInvertNodeLocation | null = locateNodeWithParent(simulation.displayItems, idNorm, "");
  if (location === null) {
    return null;
  }
  return {
    kind: "nodeMove",
    nodeId: idNorm,
    parentId: location.parentIdNorm,
    afterSiblingId: precedingSiblingId(location)
  };
}

function invertProp(simulation: IScreenDesign, op: ICollabOp): CollabOpDraft | null {
  if (typeof op.path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(op.path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return null;
  }
  const location: IInvertNodeLocation | null = locateNodeWithParent(simulation.displayItems, idNorm, "");
  if (location === null) {
    return null;
  }
  const inverse: ValueInverse | null =
    op.kind === "propSet"
      ? invertValueSet(location.node, segments, op.value)
      : invertValueRemove(location.node, segments);
  if (inverse === null) {
    return null;
  }
  if (inverse.kind === "set") {
    return { kind: "propSet", nodeId: idNorm, path: inverse.path, value: inverse.value };
  }
  return { kind: "propRemove", nodeId: idNorm, path: inverse.path };
}

function invertScreenProp(simulation: IScreenDesign, op: ICollabOp): CollabOpDraft | null {
  if (typeof op.path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(op.path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  const inverse: ValueInverse | null =
    op.kind === "screenPropSet"
      ? invertValueSet(simulation, segments, op.value)
      : invertValueRemove(simulation, segments);
  if (inverse === null) {
    return null;
  }
  if (inverse.kind === "set") {
    return { kind: "screenPropSet", path: inverse.path, value: inverse.value };
  }
  return { kind: "screenPropRemove", path: inverse.path };
}

function invertOneOp(simulation: IScreenDesign, op: ICollabOp): CollabOpDraft | null {
  switch (op.kind) {
    case "nodeAdd":
      return invertNodeAdd(op);
    case "nodeRemove":
      return invertNodeRemove(simulation, op);
    case "nodeMove":
      return invertNodeMove(simulation, op);
    case "propSet":
    case "propRemove":
      return invertProp(simulation, op);
    case "screenPropSet":
    case "screenPropRemove":
      return invertScreenProp(simulation, op);
    default:
      return null;
  }
}

/**
 * Computes the inverse op list for `ops` against `prevTree` (the tree state BEFORE
 * `ops` were applied). See the module doc for the invert law and the simulation
 * strategy. Every returned op carries a fresh id from `makeOpId` (assigned in
 * returned order) and never a `resolvedIndex`.
 */
export function invertCollabOps(prevTree: IScreenDesign, ops: ICollabOp[], makeOpId: () => string): ICollabOp[] {
  const simulation: IScreenDesign = cloneJsonValue(prevTree) as unknown as IScreenDesign;
  if (!Array.isArray(simulation.displayItems)) {
    simulation.displayItems = [];
  }
  const drafts: CollabOpDraft[] = [];
  for (const op of ops) {
    const draft: CollabOpDraft | null = invertOneOp(simulation, op);
    const result: ICollabApplyResult = applyCollabOps(simulation, [op]);
    if (result.appliedCount === 1 && draft !== null) {
      drafts.push(draft);
    }
  }
  drafts.reverse();
  return drafts.map((draft: CollabOpDraft): ICollabOp => ({ opId: makeOpId(), ...draft }));
}
